import { logError, logInfo } from '@/lib/logger'
// Direct import from the types module to avoid the circular dep with
// useSettings.ts (which imports this service). This was the runtime
// cause of `s is not a function` in the production-minified
// settings-service chunk — see comment in `@/types/settings`.
import { DEFAULT_SETTINGS, type SettingsState } from '@/types/settings'
import { backendSettingsService } from './backend-settings-service'
import { authService } from './auth-service'

/**
 * Settings Service (Azure AD)
 * Speichert und lädt Benutzereinstellungen über das FastAPI Backend.
 *
 * Postgres ist die einzige Quelle (#908); es gibt keine lokale Kopie mehr.
 *
 * Geschrieben wird ausschließlich per PATCH mit den geänderten Keys: ein
 * PUT ersetzt `settings_data` komplett, und jeder Aufrufer, der vorher
 * Standardwerte statt der echten Settings in der Hand hatte, hat damit
 * Theme, Apps und Ordner des Nutzers überschrieben.
 */
export class SettingsService {
  private static getCurrentUser() {
    return authService.getCachedUser()
  }

  /**
   * Lädt die Einstellungen des aktuellen Benutzers aus dem Backend.
   *
   * Wirft, wenn niemand angemeldet ist oder der Request scheitert.
   * Standardwerte gibt es nur, wenn das Backend für den Nutzer wirklich
   * nichts gespeichert hat (404) — ein Fehler darf nie wie „leer" aussehen,
   * sonst schreibt der nächste Aufrufer Defaults zurück.
   */
  static async loadUserSettings(): Promise<SettingsState> {
    if (!this.getCurrentUser()) {
      throw new Error('Settings können ohne angemeldeten Benutzer nicht geladen werden')
    }

    const backendSettings = await backendSettingsService.loadSettings()
    if (!backendSettings) {
      logInfo('Keine gespeicherten Einstellungen im Backend, verwende Standardeinstellungen')
      return DEFAULT_SETTINGS
    }
    return { ...DEFAULT_SETTINGS, ...backendSettings }
  }

  /**
   * Schreibt nur die übergebenen Keys ins Backend (Top-Level-Merge).
   * Liefert `false`, wenn das Backend nicht geschrieben wurde.
   */
  static async patchSettings(changes: Partial<SettingsState>): Promise<boolean> {
    if (!this.getCurrentUser()) {
      logError(new Error('Kein Benutzer angemeldet'), { context: 'Settings speichern', keys: Object.keys(changes) })
      return false
    }

    const success = await backendSettingsService.patchSettings(changes)
    if (!success) {
      logError(new Error('Backend speichern fehlgeschlagen'), { context: 'Settings speichern', keys: Object.keys(changes) })
      return false
    }

    return true
  }
}

export default SettingsService
